import { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import InputWithTooltipPlaceholder from './InputWithTooltipPlaceholder'

/**
 * Ported from pitstop-ui/src/components/EForm/FormField/cell-chip.jsx
 *
 * Changes:
 *   - `classnames` → `clsx`
 *   - @mui/material Chip replaced with a plain styled span (not installed)
 *   - x-mark.svg (ClearRoundedIcon) replaced with a '×' text button (not in submittals-ui/assets/svgs)
 *   - InputWithTooltipPlaceholder import path corrected to local ./
 */

const INPUT_BASE =
  'box-border w-full h-[45px] pt-3 px-[14px] pb-[10px] type-body-2 text-grey-10 border border-grey-90 rounded-[5px] bg-white shadow-none transition-[border-color] duration-150 ease-in-out appearance-none focus:border-theme-50 focus:outline-none disabled:text-grey-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:bg-grey-98'

function getClassName(error) {
  return clsx(INPUT_BASE, error && 'border-red-50 focus:border-red-50')
}

const CellChipComponent = ({
  error,
  isReadOnly,
  schema,
  getFieldValue,
  payload,
  parentProps,
  inputProps,
  setInputProps,
  updatePayload,
  validateSchema,
}) => {
  const [chips, setChips] = useState([])
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef()

  useEffect(() => {
    const fieldValue = getFieldValue(payload, parentProps)
    const chipValues = Array.isArray(fieldValue)
      ? fieldValue.filter(v => v !== null && v !== undefined && v !== '')
      : []
    const valueProp =
      fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
        ? { value: fieldValue }
        : {}

    setInputProps({
      ...inputProps,
      ...valueProp,
      placeholder: schema.description || schema.title || '',
      type: Array.isArray(schema.items?.type)
        ? schema.items?.type?.find(d => d !== 'null')
        : schema.items?.type,
      value: chipValues,
    })
    setChips(chipValues)
  }, [payload, isReadOnly])

  const handleKeyDown = e => {
    if (e.key === 'Backspace' && inputValue === '' && chips?.length > 0) {
      removeChip(chips?.length - 1)
      return
    }
    if (e.key !== 'Enter') return
    e.preventDefault()
    let value = inputValue.trim()
    if (value === '') {
      setInputProps({ ...inputProps })
      validateSchema(null)
      setInputValue('')
      return
    }
    value = convertValue(inputProps.type, value)
    if (value === undefined) {
      validateSchema([...chips, value])
      setInputValue('')
      return
    }
    const newChips = [...chips, value]
    setChips(newChips)
    setInputProps({ ...inputProps, value: newChips })
    updatePayload(newChips)
    validateSchema(newChips)
    setInputValue('')
  }

  const handleInputBlur = () => {
    let value = inputValue.trim()
    if (value === '') {
      setInputProps({ ...inputProps })
      validateSchema(chips.length > 0 ? chips : null)
      setInputValue('')
      return
    }
    value = convertValue(inputProps.type, value)
    if (value === undefined) {
      validateSchema(chips)
      setInputValue('')
      return
    }
    const newChips = [...chips, value]
    setChips(newChips)
    setInputProps({ ...inputProps, value: newChips })
    updatePayload(newChips)
    validateSchema(newChips)
    setInputValue('')
  }

  const convertValue = (type, value) => {
    const numberRegex = /^-?\d+(\.\d+)?$/
    if (type === 'number') {
      return numberRegex.test(value) ? Number(value) : undefined
    } else if (type === 'string') {
      return isNaN(value) ? String(value) : undefined
    }
  }

  const handleInputChange = e => {
    setInputValue(e.target.value)
  }

  const removeChip = indexToRemove => {
    const filteredChips = chips?.filter((_, index) => index !== indexToRemove)
    setChips(filteredChips)
    updatePayload(filteredChips)
    validateSchema(filteredChips.length > 0 ? filteredChips : null)
  }

  return (
    <div className="flex flex-wrap items-center justify-center overflow-hidden">
      <div
        className={clsx(
          getClassName(error),
          'flex flex-wrap items-center min-h-10 h-fit p-0 rounded gap-[5px]',
          isReadOnly &&
            'text-grey-50 cursor-not-allowed pointer-events-none border border-grey-90 bg-grey-98'
        )}
      >
        {(isReadOnly ? inputProps?.value || [] : chips)?.map((chip, index) => (
          <span
            key={index}
            data-testid={`chip-item-${index}`}
            className="!min-h-6 !max-h-[27px] inline-flex items-center border border-[#60636B] rounded-2xl py-0.5 px-2 m-0.5 text-[0.8125rem] font-medium"
          >
            {chip}
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => removeChip(index)}
                data-testid="chip-delete-icon"
                className="w-4 h-4 ml-1 bg-transparent border-0 cursor-pointer p-0 leading-none"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!isReadOnly && (
          <InputWithTooltipPlaceholder
            ref={inputRef}
            type="text"
            disabled={isReadOnly}
            data-testid="chip-input-field"
            className="grow overflow-hidden w-auto max-w-[608px] h-full pt-3 pb-3 pl-0.5 pr-3.5 text-grey-50 whitespace-nowrap text-ellipsis border-0 outline-none bg-transparent"
            value={inputValue}
            onKeyDown={isReadOnly ? null : handleKeyDown}
            onChange={isReadOnly ? null : handleInputChange}
            onBlur={isReadOnly ? null : handleInputBlur}
            placeholder={!isReadOnly && chips?.length === 0 ? inputProps?.placeholder : ''}
          />
        )}
      </div>
    </div>
  )
}

CellChipComponent.propTypes = {
  error: PropTypes.any,
  isReadOnly: PropTypes.any,
  schema: PropTypes.any,
  payload: PropTypes.any,
  parentProps: PropTypes.any,
  inputProps: PropTypes.any,
  setInputProps: PropTypes.any,
  updatePayload: PropTypes.any,
  validateSchema: PropTypes.any,
  getFieldValue: PropTypes.any,
}

export default CellChipComponent
